import client from 'libs/client';
import { useCallback, useMemo } from 'react';

export interface Club {
  value: number;
  label: string;
  country: string;
  logo: string | null;
}

const RESULTS_LIMIT = 10;

export const useClubList = () => {
  const query = client.getAllNPClubs.useQuery({
    queryKey: ['clubs'],
  });

  const clubs = query.data?.data as Club[] | undefined;

  const clubWordsMap = useMemo(() => {
    if (!clubs) {
      return new Map();
    }

    return new Map(
      clubs.map(club => [
        club,
        new Set(club.label?.toLowerCase().split(/\s+/).filter(Boolean) || [])
      ])
    );
  }, [clubs]);

  const findSimilarClubs = useCallback((searchName: string): Club[] => {
    if (!searchName.trim() || !clubs) {
      return clubs?.slice(0, RESULTS_LIMIT) || []
    };

    const searchWords = new Set(searchName.toLowerCase().split(/\s+/).filter(Boolean));
    if (searchWords.size === 0) {
      return clubs?.slice(0, RESULTS_LIMIT) || []
    };

    const matches = clubs.filter(club => {
      if (!club.label) {
        return false
      };

      const clubWords = clubWordsMap.get(club);
      if (!clubWords) {
        return false
      };

      for (const searchWord of searchWords) {
        let foundMatch = false;
        for (const clubWord of clubWords) {
          if (clubWord.includes(searchWord) || searchWord.includes(clubWord)) {
            foundMatch = true;
            break;
          }
        }

        if (!foundMatch) {
          return false
        };
      }
      return true;
    });

    return matches.slice(0, RESULTS_LIMIT) || [];
  }, [clubs, clubWordsMap]);

  return {
    clubs: clubs as Club[],
    findSimilarClubs,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch
  };
};